import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Flag, Loader2, CheckCircle, XCircle, MinusCircle, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CATEGORY_LABELS = {
  harassment: 'Harassment / abuse', scam_fraud: 'Scam / fraud',
  inappropriate_content: 'Inappropriate content', spam: 'Spam',
  no_show: 'No-show / unreliable', off_platform_payment: 'Off-platform payment', other: 'Other',
};

const ModerationTab = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({ pending: 0, resolved: 0, total: 0 });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/admin/reports?status=${filter}`, auth()),
        axios.get(`${API}/admin/reports/stats`, auth()),
      ]);
      setReports(r.data);
      setStats(s.data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id, resolution) => {
    setBusyId(id);
    try {
      const res = await axios.post(`${API}/admin/reports/${id}/resolve`, { resolution, admin_notes: notes[id] || '' }, auth());
      if (resolution === 'valid' && res.data.action_taken && res.data.action_taken !== 'none') {
        const a = res.data.action_taken;
        alert(a.startsWith('auto_suspended') ? 'Marked valid — user auto-suspended (3rd valid report).' : `Marked valid — ${a.replace('_', ' ')} issued to the user.`);
      }
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to resolve'); } finally { setBusyId(null); }
  };

  return (
    <div data-testid="moderation-tab">
      <div className="flex items-center gap-2 mb-4">
        <Flag className="w-6 h-6 text-red-500" />
        <h2 className="text-xl font-bold text-white">Reports & Moderation</h2>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-gray-800 rounded-lg p-4"><div className="text-2xl font-bold text-amber-400" data-testid="reports-pending-count">{stats.pending}</div><div className="text-xs text-gray-400">Pending review</div></div>
        <div className="bg-gray-800 rounded-lg p-4"><div className="text-2xl font-bold text-emerald-400">{stats.resolved}</div><div className="text-xs text-gray-400">Resolved</div></div>
        <div className="bg-gray-800 rounded-lg p-4"><div className="text-2xl font-bold text-gray-200">{stats.total}</div><div className="text-xs text-gray-400">Total</div></div>
      </div>

      <div className="flex gap-2 mb-4">
        {['pending', 'resolved', 'all'].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${filter === f ? 'bg-orange-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            data-testid={`reports-filter-${f}`}>{f}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-10 text-gray-400" data-testid="reports-empty">No {filter !== 'all' ? filter : ''} reports.</div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="bg-gray-800 rounded-xl p-4 border border-gray-700" data-testid={`report-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-500/20 text-red-300">{CATEGORY_LABELS[r.category] || r.category}</span>
                    <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleString()}</span>
                    {r.reported_user_valid_reports > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300"><ShieldAlert className="w-3 h-3" />{r.reported_user_valid_reports}/3 valid</span>
                    )}
                    {r.status === 'resolved' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-600 text-gray-200">{r.resolution}{r.action_taken && r.action_taken !== 'none' ? ` · ${r.action_taken.replace('_', ' ')}` : ''}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-200 mt-2">
                    <span className="text-gray-400">Reported:</span> <strong>{r.reported_user_name}</strong>
                    <span className="text-gray-500"> ({r.reported_user_status})</span>
                    <span className="text-gray-400"> · by</span> {r.reporter_name}
                  </p>
                  <p className="text-sm text-gray-300 mt-1 whitespace-pre-wrap"><span className="text-gray-400">Explanation:</span> {r.reason}</p>
                  {!r.has_evidence && r.status === 'pending' && (
                    <p className="text-xs text-amber-300 mt-1">⚠ No attached evidence — verify the explanation is substantive before marking valid.</p>
                  )}
                  {r.admin_notes && <p className="text-xs text-gray-400 mt-1 italic">Admin notes: {r.admin_notes}</p>}
                </div>
              </div>

              {r.status === 'pending' && (
                <div className="mt-3 border-t border-gray-700 pt-3">
                  <input value={notes[r.id] || ''} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                    placeholder="Internal notes (optional)…"
                    className="w-full mb-2 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm border border-gray-600"
                    data-testid={`report-notes-${r.id}`} />
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => resolve(r.id, 'valid')} disabled={busyId === r.id}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      data-testid={`report-valid-${r.id}`}>
                      {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Valid (take action)
                    </button>
                    <button onClick={() => resolve(r.id, 'invalid')} disabled={busyId === r.id}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-gray-600 text-white hover:bg-gray-500 disabled:opacity-50"
                      data-testid={`report-invalid-${r.id}`}>
                      <XCircle className="w-4 h-4" /> Invalid
                    </button>
                    <button onClick={() => resolve(r.id, 'dismissed')} disabled={busyId === r.id}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-50"
                      data-testid={`report-dismiss-${r.id}`}>
                      <MinusCircle className="w-4 h-4" /> Dismiss
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModerationTab;
